import { ClassifyCard } from './ClassifyCard'
import type { ClassifyRowView } from './row-view'
import type { PanelAxis } from './types'

export interface ClassifyGridProps {
	rows: ClassifyRowView[]
	/** All pickable axes, for option/label lookup. */
	axes: PanelAxis[]
	/** Axes a file cannot commit without. */
	requiredAxisCodes: string[]
	/** Flip once a commit has been attempted, so errors appear only then. */
	showErrors?: boolean
	onOverride: (stagedId: string, axisCode: string, valueCode: string) => void
	/** Drops a file from the batch before it is ever uploaded. */
	onRemove: (stagedId: string) => void
}

/**
 * The staged batch, one card per file.
 *
 * A grid rather than a table because the task is editing across one file, not
 * scanning down a column — and because three comboboxes never fit in a table
 * cell. Cards reuse the responsive-grid idiom the Library and campaign
 * galleries already use, so a batch of twenty reads as a contact sheet rather
 * than a spreadsheet.
 */
export function ClassifyGrid({
	rows,
	axes,
	requiredAxisCodes,
	showErrors = false,
	onOverride,
	onRemove,
}: ClassifyGridProps) {
	const requiredAxes = axes.filter((a) => requiredAxisCodes.includes(a.code))
	const optionalAxes = axes.filter((a) => !requiredAxisCodes.includes(a.code))
	const axisLabels = Object.fromEntries(axes.map((a) => [a.code, a.label]))

	// Nothing staged, nothing to say: the dropzone above already reads "Drag and
	// drop files here" and "nothing is uploaded until you commit", so an empty
	// state here would only repeat it.
	if (rows.length === 0) return null

	return (
		/* `minmax(0,1fr)` rather than Tailwind's `grid-cols-N`: a bare `1fr` track
		   floors at its content's min-content width, so one long file name widens
		   its whole column and the cards stop lining up.

		   `items-start` so a card opened for override does not stretch its
		   collapsed row-mates to match its height. */
		<ul className="grid grid-cols-[minmax(0,1fr)] items-start gap-ds-3 md:grid-cols-[repeat(2,minmax(0,1fr))] xl:grid-cols-[repeat(3,minmax(0,1fr))]">
			{rows.map((row) => (
				<ClassifyCard
					key={row.stagedId}
					row={row}
					requiredAxes={requiredAxes}
					optionalAxes={optionalAxes}
					axisLabels={axisLabels}
					showErrors={showErrors}
					onOverride={onOverride}
					onRemove={onRemove}
				/>
			))}
		</ul>
	)
}
